import React, { useCallback, useEffect, useMemo, useState } from "react";
import useIdentity from "@/features/ui/hooks/use-identity";
import useListTransportations, {
  ListTransportationsPathParameter,
  ListTransportationsRequest,
} from "@/features/transportations/hooks/use-list-transportations";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useRouterHelper from "@/features/ui/hooks/use-router-helper";
import AppPanel from "@/features/invoices/order-confirmation-invoices/app-panel";
import AppGrid, { AppGridMeta } from "@/features/order-sheet-sets/app-grid";
import AppGridCell from "@/features/order-sheet-sets/app-grid-cell";
import GridRowIndicator from "@/features/order-sheet-sets/grid-row-indicator";
import IsolatedPanelSkeleton from "@/features/invoices/isolated-panel-skeleton";
import {
  TransportationRow,
  transportationRowMapper,
} from "@/features/transportations/utils/transportation-row-mapper";
import { GridRow } from "@/features/order-sheet-sets/helpers/app-grid-row-helper";
import { Box, Flex } from "@chakra-ui/react";
import AppLabel from "@/features/ui/app-label";
import useDownloadUnoFile from "@/hooks/use-download-uno-file";
import AppIconButton from "@/features/line-sheet-sets/app-icon-button";

interface Props {
  transportationIds: number[];
  onCountChange?: (count: number) => void;
  onEdit?: (id: number) => void;
}

const rowMapper = transportationRowMapper.copy();

export default function IsolatedTransportationPanel({
  transportationIds,
  onCountChange,
  onEdit,
}: Props) {
  const identity = useIdentity();
  const { tTitle, tLocalDateString, tCurrencyString, tLocalDateTimeString } =
    useI18nHelper();
  const { navigate } = useRouterHelper();
  const { error: showError } = useAppToasts();
  const { fire: fireDownloadUnoFile } = useDownloadUnoFile();
  const handleDownloadFile = useCallback(
    (fileId: number, e: React.MouseEvent) => {
      e.stopPropagation();
      fireDownloadUnoFile({ unoFileId: fileId }, {});
    },
    [fireDownloadUnoFile]
  );

  const key = useMemo(():
    | {
        parameter: ListTransportationsPathParameter;
        request: ListTransportationsRequest;
      }
    | undefined => {
    if (identity?.company && transportationIds.length > 0) {
      return {
        parameter: {
          by: identity.company.type,
          companyId: identity.company.id,
        },
        request: {
          id__in: transportationIds,
          pageNumber: 1,
          pageSize: transportationIds.length,
        },
      };
    }
    return undefined;
  }, [identity, transportationIds]);

  const { data, error } = useListTransportations(key?.parameter, key?.request, {
    revalidateOnFocus: false,
    shouldRetryOnError: false,
  });

  const [rows, setRows] = useState<TransportationRow[] | undefined>(undefined);

  useEffect(() => {
    if (data) {
      const nextRows = rowMapper.toRows(data.transportations);
      setRows(nextRows);
      onCountChange?.(data.transportations.length);
    }
  }, [data, onCountChange]);

  // 부티크는 국내 배송만 다루므로 목록에 국내배송사·송장번호만 보여준다(항공편·운송비는 에이전시 몫).
  const isBoutique = identity?.company?.type === "BOUTIQUE";

  const metas = useMemo(
    (): AppGridMeta[] => [
      {
        rowType: "Transportation",
        gap: "8px",
        columns: isBoutique
          ? [
              { name: tTitle("domestic_carrier_name"), width: "1fr" },
              { name: tTitle("domestic_tracking_number"), width: "1fr" },
            ]
          : [
          { name: tTitle(""), width: "1fr" },
          { name: tTitle("flight_eta"), width: "1fr" },
          { name: tTitle("transportation_payment"), width: "1fr" },
        ],
        fields: isBoutique
          ? [
              {
                name: "domestic_carrier_name",
                value: (_row) => {
                  const row = _row as TransportationRow;
                  return (
                    <AppGridCell
                      value={[
                        <Box key={"domestic_carrier_name"} fontWeight={"700"}>
                          {row.domesticCarrierName ?? "-"}
                        </Box>,
                      ]}
                    />
                  );
                },
              },
              {
                name: "domestic_tracking_number",
                value: (_row) => {
                  const row = _row as TransportationRow;
                  return (
                    <AppGridCell
                      value={[
                        <Box key={"domestic_tracking_number"}>
                          {row.domesticTrackingNumber ?? "-"}
                        </Box>,
                      ]}
                    />
                  );
                },
              },
            ]
          : [
          {
            name: "departure",
            value: (_row) => {
              const row = _row as TransportationRow;
              return (
                <AppGridCell
                  value={[
                    <Box key={"airport"} fontWeight={"700"}>
                      {row.name}
                    </Box>,
                  ]}
                />
              );
            },
          },
          {
            name: "flight_eta",
            value: (_row) => {
              const row = _row as TransportationRow;
              return (
                <AppGridCell
                  value={[
                    <Box key={"airport"} fontWeight={"700"}>
                      {row.flightEta}
                    </Box>,
                  ]}
                />
              );
            },
          },
          {
            name: "transportation_payment",
            value: (_row) => {
              const row = _row as TransportationRow;
              if (row.transportationPayment) {
                return (
                  <AppGridCell
                    value={[
                      <Flex key={"transportation_payment_amount"} gap="8px">
                        {tCurrencyString(row.transportationPayment.amount, {
                          type: "SYMBOL",
                          code: "KRW",
                        })}
                        <AppLabel
                          nameKey={
                            row.transportationPayment?.isRemitted
                              ? "송금 완료"
                              : "송금 필요"
                          }
                          level={
                            row.transportationPayment?.isRemitted
                              ? "SUCCESS"
                              : "ERROR"
                          }
                          size={"small"}
                        />
                      </Flex>,
                      <Flex
                        key={"transportation_payment_file"}
                        gap="4px"
                        alignItems="center"
                      >
                        {row.transportationPayment.file?.name}
                        {row.transportationPayment.file && (
                          <AppIconButton
                            ariaLabel={"ghost"}
                            icon={"download"}
                            size={"medium"}
                            onClick={(e) =>
                              handleDownloadFile(
                                row.transportationPayment!.file!.id,
                                e
                              )
                            }
                          />
                        )}
                      </Flex>,
                    ]}
                  />
                );
              } else {
                return (
                  <AppGridCell
                    value={[
                      <Box key={"transportation_payment"} fontWeight={"700"}>
                        업로드 필요
                      </Box>,
                    ]}
                  />
                );
              }
            },
          },
        ],
        indicator: (row: GridRow) => (
          <GridRowIndicator
            type={"VIEW"}
            isCollapsed={row.isCollapsed}
            isHovered={row.isHovered}
          />
        ),
        onClick: (row: GridRow) => {
          if (onEdit) {
            onEdit(row.id);
          } else {
            navigate(`/transportations/${row.id}`);
          }
        },
      },
    ],
    [
      isBoutique,
      tTitle,
      tCurrencyString,
      handleDownloadFile,
      onEdit,
      navigate,
    ]
  );

  if (!data) {
    return <IsolatedPanelSkeleton />;
  }

  if (!rows || rows.length === 0) {
    return null;
  }

  return (
    <AppPanel variant={"ghost"} isCompact={true}>
      <AppGrid isAnchored={false} rows={rows} metas={metas} />
    </AppPanel>
  );
}
